import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useHapticFeedback } from '../hooks/useTelegram';
import './OnboardingPage.css';

interface OnboardingPageProps {
  onComplete: () => void;
}

const OnboardingPage: React.FC<OnboardingPageProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { notification } = useHapticFeedback();
  const navigate = useNavigate();

  const totalSteps = 3;

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleCompleteAndNavigate = useCallback(async () => {
    try {
      setLoading(true);
      await apiClient.completeOnboarding(false);
      
      notification('success');
      const redirectPath = await apiClient.getPostOnboardingRedirectPath();
      navigate(redirectPath);
      onComplete();
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      notification('error');
    } finally {
      setLoading(false);
    }
  }, [onComplete, notification, navigate]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1 onNext={handleNext} loading={loading} />;
      case 2:
        return <Step2 onNext={handleNext} onBack={handleBack} loading={loading} />;
      case 3:
        return <Step3 onComplete={handleCompleteAndNavigate} onBack={handleBack} loading={loading} />;
      default:
        return null;
    }
  };

  return (
    <div className="onboarding-page">
      <div className="progress-dots" key={currentStep}>
        {[1, 2, 3].map((step) => (
          <div
            key={step}
            className={`progress-dot${step < currentStep ? ' completed' : ''}${step === currentStep ? ' filling' : ''}`}
          />
        ))}
      </div>
      <div className="onboarding-content">
        {renderStep()}
      </div>
    </div>
  );
};

// Step 1: About the Project
const Step1: React.FC<{ 
  onNext: () => void; 
  loading: boolean;
}> = ({ onNext, loading }) => {
  return (
    <div className="onboarding-step first-step">
      <div className="step-header">
        <h2 className="step-title">Ху#NFT — забудь всё, что ты знал о цифровом искусстве!</h2>
        <div className="step-image-center">
          <img src="/onb1.png" alt="О проекте" className="step-inline-image" />
        </div>
        <div className="step-description">
          <p>Хватит покупать фуфло — бери оригинально <span className="highlight-word">ху#вые цифровые работы</span></p>
          <p>Врывайся в самые <span className="highlight-word">ху#вые форматы</span> коллекционирования</p>
          <p>Сделай коллекционирование, бл# <span className="highlight-word">выгодным</span> и <span className="highlight-word">интересным</span></p>
        </div>
      </div>
      <div className="step-footer">
        <button className="primary-button" onClick={onNext} disabled={loading}>
          Далее
        </button>
      </div>
    </div>
  );
};

// Step 2: Concept
const Step2: React.FC<{ 
  onNext: () => void; 
  onBack: () => void;
  loading: boolean;
}> = ({ onNext, onBack, loading }) => {
  return (
    <div className="onboarding-step">
      <div className="step-header">
        <h2 className="step-title" style={{ position: 'static', transform: 'none', width: 'auto', textAlign: 'center' }}>Каждое приобретение ломает скуку и приносит ценность!</h2>
      </div>
      <div className="step-body">
        <div className="concept-content">
          <div className="step-image-center">
            <img src="/onb2.png" alt="Концепция" className="step-inline-image" />
          </div>
          <p className="concept-text">
            Мы сделали то, о чём все думали и чего стеснялись. Это НЕ искусство это ху#ня
          </p>
          <p className="concept-text">
            Каждая NFT — маленький шок-арт, за которым стоит реальный объект, и ты думаешь
          </p>
          <p className="concept-text">
            <span className="highlight-word">«что за ху#ня?!»</span> Пеньки? <span className="highlight-word">Стали ху#ки</span>
          </p>
        </div>
      </div>
      <div className="step-footer">
        <button 
          className="primary-button" 
          onClick={onNext}
          disabled={loading}
        >
          Далее
        </button>
        <button 
          className="secondary-button" 
          onClick={onBack}
          disabled={loading}
        >
          Назад
        </button>
      </div>
    </div>
  );
};

// Step 3: How the Main Mechanics Work
const Step3: React.FC<{ 
  onComplete: () => Promise<void>; 
  onBack: () => void;
  loading: boolean;
}> = ({ onComplete, onBack, loading }) => {
  return (
    <div className="onboarding-step">
      <div className="step-header">
        <h2 className="step-title" style={{ position: 'static', transform: 'none', width: 'auto', textAlign: 'center' }}>Как получить максимум от участия?</h2>
      </div>
      <div className="step-body">
        <div className="mechanics-content">
          <div className="mechanics-card">
            <img src="/emo1.png" alt="" className="mechanics-emoji" />
            <p className="mechanics-card-text">Заработай на своём вкусе — и преврати хотелки в бабки! Как только ты покупаешь — <span className="highlight-word">цена ху#NFT удваивается!</span></p>
          </div>
          <div className="mechanics-card">
            <img src="/emo2.png" alt="" className="mechanics-emoji" />
            <p className="mechanics-card-text">Купил ху#NFT? Он сам ху#ячит на продажу — тебе вообще делать ничего не надо <span className="highlight-word">ху#вая система сделает всё за тебя!</span></p>
          </div>
          <div className="mechanics-card">
            <img src="/emo3.png" alt="" className="mechanics-emoji" />
            <p className="mechanics-card-text">Каждый твой день в проекте — это ху#вый шанс срубить <span className="highlight-word">призы от крутых брендов!</span></p>
          </div>
        </div>
      </div>
      <div className="step-footer">
        <button 
          className="primary-button" 
          onClick={onComplete}
          disabled={loading}
        >
          {loading ? 'Загрузка...' : 'Забрать ху#к'}
        </button>
        <button 
          className="secondary-button" 
          onClick={onBack}
          disabled={loading}
        >
          Назад
        </button>
      </div>
    </div>
  );
};

export default OnboardingPage;
